// useTipHold — the grace period that lets a pointer travel from a chart shape into
// its tooltip.
//
// A chart closes its tooltip on the shape's own mouseleave, which is correct for an
// inert card. The moment the card holds a CONTROL, that rule makes the control
// unreachable: ChartTooltip sits 16px clear of the pointer, so the pointer must leave
// the bar to get there, and the card is gone before it arrives.
//
// So leaving a shape SCHEDULES the close instead of doing it, and the card cancels
// that schedule when the pointer reaches its control (ChartTooltip's `onHold`).
// Leaving the control, or the chart entirely, closes at once — by then the pointer
// has answered the question.
//
// The delay covers the whole walk from the shape to the control, not just the 16px
// gap: the card is centred on the pointer, so its footer button sits about half a
// card BELOW the cursor and the trip is ~100px of deliberate diagonal movement. Too
// short and the card closes mid-approach; this is sized for an unhurried hand.
import { useCallback, useEffect, useRef } from 'react';

/** Milliseconds a tooltip stays up after the pointer leaves the shape. */
export const TIP_GRACE_MS = 420;

/**
 * @param {Function} clear    closes the tooltip (clears the chart's hover state)
 * @param {boolean}  enabled  false → `scheduleClose` closes at once, i.e. the
 *                            pre-existing behaviour for a non-interactive card
 * @returns {{ scheduleClose: Function, cancelClose: Function, closeNow: Function }}
 */
export function useTipHold(clear, enabled = false) {
  const timer = useRef(null);
  // The latest `clear` without re-creating the callbacks on every render — charts
  // pass an inline arrow, and a new `closeNow` identity each render would re-sync
  // Recharts' internal bar settings.
  const clearRef = useRef(clear);
  clearRef.current = clear;

  const cancelClose = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const closeNow = useCallback(() => {
    cancelClose();
    clearRef.current?.();
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    if (!enabled) {
      closeNow();
      return;
    }
    cancelClose();
    timer.current = setTimeout(() => {
      timer.current = null;
      clearRef.current?.();
    }, TIP_GRACE_MS);
  }, [enabled, cancelClose, closeNow]);

  // A pending close must not fire into an unmounted chart.
  useEffect(() => cancelClose, [cancelClose]);

  return { scheduleClose, cancelClose, closeNow };
}

export default useTipHold;
